import React, { useState, useMemo, useEffect } from 'react';
import { Box } from '@mui/material';
import { Table, createColumnHelper } from '@bestdoctor/spectrum-uikit/Table';
import type { ColumnDef } from '@tanstack/react-table';
import DescriptionIcon from '@mui/icons-material/Description';
import { Text } from '@bestdoctor/spectrum-uikit/Text';
import { Loader } from 'shared/loaders/Loader';
import { ITablePaymentProps, ITablePaymentData } from 'interfaces/finance/financeInterfaces';
import { tableCellStatusStyle } from 'ui/tables/tables';
import { moneyRowCurrentView, throttle } from 'utils/tablesMethods';

const columnHelper = createColumnHelper<ITablePaymentData>();


const TablePayment: React.FC<ITablePaymentProps> = ({
  dataPayments,
  hasNextPagePayments,
  isFetchingNextPagePayments,
  fetchNextPage,
}) => {
  const rows: ITablePaymentData[] = useMemo(
    () =>
      dataPayments
        .map((item: ITablePaymentData) => ({
          periodPay: item.periodPay,
          beforeDatePay: item.beforeDatePay,
          actualDatePay: item.actualDatePay,
          amountPay: item.amountPay,
          statusPay: item.statusPay,
          basisPay: item.basisPay,
          billDate: item.billDate,
          billFile: item.billFile,
        }))
        .filter((row) => row.amountPay !== 0),
    [dataPayments]
  );

  const [sorting, setSorting] = useState<{ id: string; desc: boolean }[]>([]);

  const tableSorting = useMemo(() => {
    if (sorting.length === 0) return undefined;
    return {
      state: sorting,
      setState: setSorting,
    };
  }, [sorting]);

  const handleScroll = (evt: Event): void => {
    const { scrollHeight, scrollTop } = (evt.target as Document).documentElement;
    const windowHeight = window.innerHeight;

    if (scrollHeight - (windowHeight + scrollTop) < 50) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (hasNextPagePayments && !isFetchingNextPagePayments) {
      document.addEventListener('scroll', throttle(handleScroll, 1000));
    }

    return () => document.removeEventListener('scroll', throttle(handleScroll, 1000));
  }, [hasNextPagePayments, isFetchingNextPagePayments, fetchNextPage]);

  const columns = useMemo<ColumnDef<ITablePaymentData, any>[]>(
    () => [
      columnHelper.accessor('periodPay', {
        id: 'periodPay',
        header: () => <Text as="span" variant="body-m">Период платежа</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue()}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
      columnHelper.accessor('beforeDatePay', {
        id: 'beforeDatePay',
        header: () => <Text as="span" variant="body-m">План. дата платежа</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue() || '-'}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
      columnHelper.accessor('actualDatePay', {
        id: 'actualDatePay',
        header: () => <Text as="span" variant="body-m">Факт. дата платежа</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue() || '-'}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
      columnHelper.accessor('amountPay', {
        id: 'amountPay',
        header: () => <Text as="span" variant="body-m">Сумма платежа, ₽</Text>,
        cell: (info) => <Text as="span" variant="body-m">{moneyRowCurrentView(info.getValue())}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
      columnHelper.accessor('statusPay', {
        id: 'statusPay',
        header: () => <Text as="span" variant="body-m">Статус</Text>,
        cell: (info) => {
          const row = info.row.original;
          return (
            <Box sx={tableCellStatusStyle(row)}>
              <Text as="span" variant="body-m">{row.amountPay > 0 ? row.statusPay : 'Возврат'}</Text>
            </Box>
          );
        },
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
      columnHelper.accessor('basisPay', {
        id: 'basisPay',
        header: () => <Text as="span" variant="body-m">Основание</Text>,
        cell: (info) => <Text as="span" variant="body-m">{info.getValue() || 'Договор'}</Text>,
        enableSorting: true,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
      columnHelper.accessor('billDate', {
        id: 'billDate',
        header: () => <Text as="span" variant="body-m">Счет</Text>,
        cell: (info) => {
          const row = info.row.original;
  return (
            <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: row.billFile !== '-' ? 'pointer' : 'default',
              }}
              onClick={() => {
                if (row.billFile !== '-') {
                  window.open(row.billFile.toString());
                }
              }}
                    >
                      <DescriptionIcon
                        sx={{
                          marginRight: '6px',
                          width: '14px',
                          height: '14px',
                          color: '#BCBEC8',
                        }}
                      />
              <Text as="span" variant="body-m">{row.billDate ? `Счет ${row.billDate}` : '-'}</Text>
            </Box>
          );
        },
        enableSorting: false,
        meta: {
          headerCellProps: {
            textVariant: 'body-m',
          },
        },
      }) as ColumnDef<ITablePaymentData, any>,
    ],
    []
  );

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
      }}
    >
      <Table
        data={rows}
        columns={columns}
        sorting={tableSorting}
        enableSorting
      />
      {isFetchingNextPagePayments && <Loader />}
    </Box>
  );
};

export { TablePayment };
